import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../api';

const STATUS_STYLE = {
  PENDING:   { bg: '#fef3c7', color: '#92400e', icon: 'fa-clock' },
  RUNNING:   { bg: '#dbeafe', color: '#1e40af', icon: 'fa-spinner fa-spin' },
  COMPLETED: { bg: '#d1fae5', color: '#065f46', icon: 'fa-check-circle' },
  FAILED:    { bg: '#fee2e2', color: '#991b1b', icon: 'fa-times-circle' },
};

function duration(start, end) {
  if (!start) return '—';
  const a = new Date(start);
  const b = end ? new Date(end) : new Date();
  const secs = Math.round((b - a) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

export default function ScanHistoryTab({ orgId }) {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const load = useCallback(() => {
    const params = { page, size: 20 };
    if (orgId) params.orgId = orgId;
    axios.get('/iasm/scans', { params })
      .then(r => { setJobs(r.data.content); setTotal(r.data.totalElements); })
      .catch(() => {});
  }, [page, orgId]);

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <span style={{ fontSize: '14px', color: '#374151' }}><strong>{total}</strong> {t('ScanHistoryTab.totalScansLabel')}</span>
        <button onClick={load} style={{
          padding: '6px 14px', border: '1px solid #d1d5db', background: '#fff',
          borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
        }}>
          <i className="fas fa-sync" style={{ marginRight: '6px' }} />{t('ScanHistoryTab.refresh')}
        </button>
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['#', t('ScanHistoryTab.colConnector'), t('ScanHistoryTab.colTrigger'), t('ScanHistoryTab.colStatus'), t('ScanHistoryTab.colDuration'), t('ScanHistoryTab.colAssets'), t('ScanHistoryTab.colEdges'), t('ScanHistoryTab.colFindings'), t('ScanHistoryTab.colStarted')].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.map(j => {
              const s = STATUS_STYLE[j.status] || STATUS_STYLE.PENDING;
              return (
                <tr key={j.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 14px', color: '#9ca3af' }}>#{j.id}</td>
                  <td style={{ padding: '10px 14px' }}>
                    {j.connector ? (
                      <div>
                        <div style={{ fontWeight: 500, color: '#111827' }}>{j.connector.displayName}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{j.connector.connectorType}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{j.triggeredBy}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ ...s, padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                      <i className={`fas ${s.icon}`} style={{ marginRight: '4px' }} />{j.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{duration(j.startedAt, j.completedAt)}</td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{j.assetsDiscovered}</td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{j.edgesMapped}</td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>{j.findingsGenerated}</td>
                  <td style={{ padding: '10px 14px', color: '#6b7280' }}>
                    {j.startedAt ? new Date(j.startedAt).toLocaleString() : '—'}
                  </td>
                </tr>
              );
            })}
            {jobs.length === 0 && (
              <tr><td colSpan={9} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>{t('ScanHistoryTab.emptyState')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {total > 20 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center' }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: page === 0 ? 'default' : 'pointer', background: '#fff' }}>
            {t('ScanHistoryTab.prev')}
          </button>
          <span style={{ alignSelf: 'center', fontSize: '13px', color: '#6b7280' }}>{t('ScanHistoryTab.pageLabel', { page: page + 1 })}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * 20 >= total}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #e5e7eb', cursor: (page + 1) * 20 >= total ? 'default' : 'pointer', background: '#fff' }}>
            {t('ScanHistoryTab.next')}
          </button>
        </div>
      )}
    </div>
  );
}
